import { useFirestore } from '../../firebase/useFirestore';
import useAutosaveField from '../../hooks/useAutosaveField';
import AutosaveStatus from '../common/AutosaveStatus';
import ToggleButton from '../common/ToggleButton';
import Table from '../common/table/Table.jsx';
import TableCell from '../common/table/TableCell.jsx';
import { allStatusesHidden } from '../../helpers/statusConfig/resolveStatusDisplay';
import resolveStatusColors from '../../helpers/statusConfig/resolveStatusColors';
import { PALETTE_COLORS, COLOR_NAMES, isPaletteColor } from '../../helpers/colorPalette';

// Beheerscherm voor de weergavelaag over een statuslijst: een eigen woord per
// status, en statussen die de organisatie niet gebruikt uit beeld halen.
//
// Wat hier NIET kan, en met opzet: een status toevoegen, verwijderen of
// verplaatsen. De statuslijst is gedeeld en de portfolio-optelling telt
// statussen over organisaties heen op — een eigen lijst maakt die optelling
// betekenisloos. Zie ADR-032.
//
// AI-CONTEXT: See docs/decisions.md#adr-032
// AI-CONTEXT: See docs/features/tenant-status-and-column-config.md

/** Ruim voor een eigen woord, krap genoeg om een tab niet te laten uitlopen. */
const MAX_LABEL_LENGTH = 40;

/**
 * @param {object} props
 * @param {string} props.title - Kop boven het blok
 * @param {string} props.description - Uitleg onder de kop, in gewone taal
 * @param {Record<string, string>} props.canonicalLabels - `ADRESS_STATUS_LABELS` of `PERSONA_STATUS_LABELS`
 * @param {string} props.field - Veld op CompagnyMeta: 'AdressStatusConfig' of 'PersonaStatusConfig'
 * @param {Record<string, string>} [props.colorDefaults] - Standaardkleur per status; meegeven zet de kolom KLEUR aan (alleen huishoudens: die kleur staat als bolletje in de agenda)
 * @returns {JSX.Element}
 */
const StatusConfigTable = ({ title, description, canonicalLabels, field, colorDefaults }) => {
    const compagny = useFirestore('CompagnyMeta');
    const { save, status } = useAutosaveField('CompagnyMeta');

    const comp = Array.isArray(compagny) ? compagny[0] : undefined;
    const config = (comp && comp[field]) || {};

    // Niet per toetsaanslag schrijven: Firestore zet schrijfacties naar
    // hetzelfde document achter elkaar in de rij, en wie wegnavigeert verliest
    // wat er nog in staat. useAutosaveField wacht tot de gebruiker even stopt
    // én schrijft alsnog weg bij het verlaten van de pagina.
    // AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
    const labelHandler = (key, value) => {
        save(comp.docid, { [`${field}.${key}.Label`]: value });
    };

    // Kleur per status, alleen waar de aanroeper standaarden meegeeft. Dezelfde
    // lezing als de agenda (resolveStatusColors), zodat de keuzelijst toont wat
    // het bolletje tekent.
    const colors = colorDefaults ? resolveStatusColors(colorDefaults, config) : null;
    const colorHandler = (key, value) => {
        if (!isPaletteColor(value)) return;
        save(comp.docid, { [`${field}.${key}.Color`]: value });
    };

    // De helper negeert een instelling die álles zou verbergen. Dat is de
    // juiste uitkomst, maar zonder deze melding ziet de gebruiker alleen dat er
    // niets gebeurt — en dat leest als een storing.
    // Dezelfde lezing als de helper die het geval negeert — niet hier opnieuw
    // uitgerekend, anders kan de melding verschijnen terwijl er wél verborgen
    // wordt.
    const allesVerborgen = allStatusesHidden(canonicalLabels, config);

    const rows = Object.keys(canonicalLabels).map((key) => ({ key, canonical: canonicalLabels[key] }));

    // `dashboard-container` en NIET `table-container`: dat laatste was tot
    // 2026-10-14 een scrollvak van 520px hoog, bedoeld om alleen een tabel in te
    // zetten. Alles eronder viel buiten beeld en was alleen nog met een
    // scrollbalk BINNEN de kaart te bereiken. De gedeelde `Table` regelt zijn
    // eigen hoogte, dus er gaat niets verloren.
    // AI-CONTEXT: See docs/pitfalls.md#ui-container-class-is-a-scroll-box
    return (
        <div className="dashboard-container">
            <h2>{title}</h2>
            <p>{description}</p>

            {/* Zolang de instellingen onderweg zijn staat er 'wordt geladen' en
                geen lege tabel: een leeg veld zou beweren dat er geen eigen naam
                is ingesteld, en dat weten we op dat moment niet.
                AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
            {!comp ? (
                <p className="status-config-loading">Instellingen worden geladen…</p>
            ) : (
                <>
                    {/* Alleen 'Opslaan…' en 'Opgeslagen'. De ruststand
                        ('Wijzigingen worden vanzelf bewaard') staat er niet: die
                        legt uit wat er nog moet gebeuren en niet wat er gebeurd
                        is, en op een scherm met alleen schakelaars en velden is
                        dat ruis. De terugkoppeling ná een wijziging blijft —
                        dit scherm heeft geen opslaanknop, dus zonder die melding
                        is er geen enkel signaal dat het gelukt is. Zie
                        docs/inbox.md #162. `reserve`: de regel staat er in rust
                        leeg, anders zakt de tabel bij de eerste opslag een regel
                        (testronde 2026-10-08, B3). */}
                    <AutosaveStatus status={status} reserve />

                    {allesVerborgen && (
                        <p className="status-config-warning">
                            Alle statussen verbergen kan niet — dan zou niemand nog een status
                            kunnen kiezen. Zolang dit zo staat, wordt er niets verborgen.
                        </p>
                    )}

                    <Table
                        columns={[
                            { key: 'canonical', label: 'STATUS' },
                            { key: 'label', label: 'EIGEN NAAM', searchable: false },
                            ...(colors ? [{ key: 'color', label: 'KLEUR', searchable: false }] : []),
                            { key: 'hidden', label: 'VERBERGEN', searchable: false },
                        ]}
                        items={rows}
                        getItemKey={(row) => row.key}
                        showSearch={false}
                        tableClassName="status-config-table"
                        renderRow={(row) => (
                            <>
                                <TableCell>{row.canonical}</TableCell>
                                <TableCell>
                                    {/* Ongecontroleerd veld met defaultValue: een
                                        gecontroleerd veld dat op zijn eigen waarde
                                        gesleuteld staat verliest de focus bij elke
                                        snapshot.
                                        AI-CONTEXT: See docs/pitfalls.md#ui-tabletitlecell-onchange-refocus */}
                                    <input
                                        type="text"
                                        maxLength={MAX_LABEL_LENGTH}
                                        placeholder={row.canonical}
                                        defaultValue={config[row.key]?.Label || ''}
                                        onChange={(e) => labelHandler(row.key, e.target.value)}
                                        aria-label={`Eigen naam voor '${row.canonical}'`}
                                    />
                                </TableCell>
                                {colors && (
                                    <TableCell>
                                        {/* Bolletje als voorbeeld naast de keuzelijst:
                                            precies wat het agendablok tekent. */}
                                        <span className={`status-dot status-dot--${colors[row.key]}`} />
                                        <select
                                            value={colors[row.key]}
                                            onChange={(e) => colorHandler(row.key, e.target.value)}
                                            aria-label={`Kleur van '${row.canonical}'`}
                                        >
                                            {PALETTE_COLORS.map((c) => <option key={c} value={c}>{COLOR_NAMES[c]}</option>)}
                                        </select>
                                    </TableCell>
                                )}
                                <TableCell>
                                    {/* Dotted veldpad: Firestore leest dit als
                                        AdressStatusConfig → <sleutel> → Hidden en
                                        maakt de tussenliggende mappen zelf aan. */}
                                    <ToggleButton
                                        docid={comp.docid}
                                        id={`${field}.${row.key}.Hidden`}
                                        collection={'CompagnyMeta'}
                                        value={config[row.key]?.Hidden}
                                    />
                                </TableCell>
                            </>
                        )}
                    />
                </>
            )}
        </div>
    );
};

export default StatusConfigTable;
